/** V3.3. Merchant-approved product-video autoplay independent of Reduce Motion.
 * Muted/inline playback, manual pause, visibility checks and browser-policy limits remain.
 * The real motion preference is retained for gallery transitions and scrolling.
 * No product, cart, price or wishlist data writes. */
(() => {
  'use strict';
  customElements.whenDefined('londa-product').then(() => {
    const P = customElements.get('londa-product').prototype;
    if (P.londaV31) return;
    P.londaV31 = true;
    const refine = P.refineV3, click = P.handleClick, disconnect = P.disconnectedCallback;
    const states = new WeakMap();
    const isJa = () => !document.documentElement.lang.toLowerCase().startsWith('en');
    const stateOf = video => {
      if (!states.has(video)) states.set(video, { pending: null, tries: 0, blocked: false, pausedByUser: false, visible: false, internalPause: false, retry: null, lastError: '', started: false });
      return states.get(video);
    };
    const playIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4.5v15l12-7.5z"/></svg>';
    const pauseIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';
    P.refineV3 = function () { refine.call(this); this.refineV31(); };
    P.refineV31 = function () {
      if (!this.ready || this.v31Ready) return;
      this.v31Ready = true; this.dataset.lpdVersion = '3.3';
      const line = this.querySelector('[data-lpd-top-line]');
      const info = this.querySelector('.lpd-info'), options = this.querySelector('.lpd-refinement-options');
      // V3 inserts options before the first promotion; keep those inside the purchase column when LINE moves to the page top.
      if (info && options && options.parentElement !== info) {
        const anchor = info.querySelector('.lpd-help-links');
        if (anchor) anchor.before(options); else info.append(options);
      }
      if (line && this.firstElementChild !== line) this.prepend(line);
      this.querySelectorAll('.lpd-coupon,.lpd-confirm-note').forEach(el => el.remove());
      const clean = () => this.cleanDescriptionV31();
      this.cleanDescriptionV31();
      this.descriptionObserverV31 = new MutationObserver(clean);
      const details = this.querySelector('.lpd-details');
      if (details) this.descriptionObserverV31.observe(details, { childList: true, subtree: true, characterData: true });
      this.on(document, 'stationTabsInstanceReady', clean);
      this.mediaNodes.forEach((node, index) => {
        const video = node.querySelector('video');
        if (!video) return;
        const state = stateOf(video); state.tries = 0; state.blocked = false;
        delete video.dataset.lpdAutoplayBlocked;
        video.playsInline = true; video.setAttribute('playsinline', ''); video.setAttribute('webkit-playsinline', '');
        if (index === 0) {
          video.muted = true; video.defaultMuted = true; video.setAttribute('muted', '');
          video.loop = true; video.autoplay = true; video.preload = 'auto';
          video.setAttribute('loading', 'eager');
        }
        let changed = false;
        video.querySelectorAll('source[data-src]').forEach(source => {
          const src = source.getAttribute('src');
          if ((!src || src.startsWith('data:')) && source.dataset.src) { source.src = source.dataset.src; changed = true; }
        });
        if (video.dataset.src && (!video.getAttribute('src') || video.getAttribute('src').startsWith('data:'))) {
          video.src = video.dataset.src; changed = true;
        }
        if (changed) video.load();
        const sync = () => { this.syncVideoControlV31(video); this.updateAutoplayReportV32(); };
        this.on(video, 'play', sync);
        this.on(video, 'playing', () => {
          state.started = true; state.blocked = false; state.lastError = ''; state.pending = null;
          state.tries = 0; clearTimeout(state.retry); state.retry = null; sync();
        });
        this.on(video, 'pause', () => {
          // Native desktop controls must also be able to pause intentionally.
          if (!state.internalPause && video.controls && state.started && this.mediaVisibleV32(video)) state.pausedByUser = true;
          sync();
        });
        this.on(video, 'ended', sync);
        ['loadeddata', 'canplay', 'loadedmetadata'].forEach(name => this.on(video, name, () => {
          this.updateMediaPlayback(); sync();
        }));
        this.on(video, 'error', () => {
          state.pending = null; state.lastError = 'MediaError:' + (video.error?.code || 'unknown'); sync();
        });
        sync();
      });
      const schedule = () => {
        if (this.autoplayFrameV32) return;
        this.autoplayFrameV32 = requestAnimationFrame(() => {
          this.autoplayFrameV32 = 0; if (this.isConnected) this.updateMediaPlayback();
        });
      };
      // iOS toolbar changes, delayed layout, and back/forward restore need a fresh visibility calculation.
      this.on(window, 'scroll', schedule, { passive: true });
      this.on(window, 'resize', schedule, { passive: true });
      this.on(window.visualViewport, 'resize', schedule, { passive: true });
      this.on(window.visualViewport, 'scroll', schedule, { passive: true });
      this.on(window, 'load', schedule);
      this.on(window, 'pageshow', () => {
        this.mediaNodes.forEach(node => { const v = node.querySelector('video'); if (v) {
          const state = stateOf(v); state.pending = null; state.tries = 0; state.blocked = false;
        } });
        schedule();
      });
      this.on(window, 'pagehide', () => this.pauseAllMedia());
      this.autoplayTimersV32 = [400, 1800, 8000, 20000].map(ms => setTimeout(() => {
        if (this.isConnected) { this.updateMediaPlayback(); this.updateAutoplayReportV32(); }
      }, ms));
      // The custom-element registry captures lifecycle callbacks at definition time; use the base controller for teardown.
      this.signal.addEventListener('abort', () => {
        (this.autoplayTimersV32 || []).forEach(clearTimeout); cancelAnimationFrame(this.autoplayFrameV32);
        this.autoplayFrameV32 = 0; this.autoplayReportV32?.remove(); this.autoplayReportV32 = null;
        this.descriptionObserverV31?.disconnect(); this.v31Ready = false; this.v3Ready = false;
      }, { once: true });
      this.setupAutoplayReportV32();
      this.updateMediaPlayback();
    };
    P.cleanDescriptionV31 = function () {
      const details = this.querySelector('.lpd-details'); if (!details) return;
      const heading = details.querySelector('.lpd-description-heading'); if (heading && heading.textContent !== 'Description') heading.textContent = 'Description';
      const exact = text => /^description\s*[:：]?$/i.test(text.replace(/[\u200b-\u200d\ufeff]/g, '').trim());
      // Tabs Studio can insert both a desktop tab and a mobile accordion title after the first render.
      details.querySelectorAll('h1,h2,h3,h4,h5,h6,p,div,span,strong,b,u,a,button,li,[role="tab"]').forEach(el => {
        if (el === heading || el.classList.contains('lpd-description') || el.contains(heading) || el.hasAttribute('data-lpd-duplicate-description')) return;
        if (!exact(el.textContent) || el.querySelector('img,video,table,input,textarea')) return;
        const links = [...el.querySelectorAll('a[href]')]; if (el.matches('a[href]')) links.push(el);
        const external = links.some(link => {
          if (link.getAttribute('role') === 'tab' || link.closest('[role="tablist"]')) return false;
          try { const u = new URL(link.href, location.href); return !u.hash || u.origin !== location.origin || u.pathname !== location.pathname; } catch (_) { return true; }
        });
        if (external) return; // Retain genuine product-description hyperlinks.
        const controls = el.matches('[aria-controls],a[href]') ? [el] : [...el.querySelectorAll('[aria-controls],a[href]')];
        controls.forEach(control => {
          let id = control.getAttribute('aria-controls');
          if (!id && control.matches('a[href]')) { try { id = new URL(control.href, location.href).hash.slice(1); } catch (_) {} }
          const panel = id && document.getElementById(id);
          if (panel && details.contains(panel) && panel.matches('[role="tabpanel"],.station-tabs-tabpanel')) {
            this.attr(panel, 'data-lpd-description-panel', ''); panel.hidden = false;
            this.attr(panel, 'aria-hidden', 'false'); this.attr(panel, 'role', 'region');
            panel.removeAttribute('aria-labelledby'); this.attr(panel, 'aria-label', 'Description');
          }
        });
        this.attr(el, 'data-lpd-duplicate-description', '');
      });
    };
    P.syncVideoControlV31 = function (video) {
      const node = video.closest('[data-lpd-media]'), toggle = node?.querySelector('[data-lpd-video-toggle]');
      if (!node) return;
      this.attr(node, 'data-lpd-playing', String(!video.paused && !video.ended));
      if (!toggle) return;
      const paused = video.paused || video.ended, icon = paused ? playIcon : pauseIcon;
      if (toggle.dataset.lpdControlState !== String(paused) || !toggle.querySelector('svg')) { toggle.innerHTML = icon; toggle.dataset.lpdControlState = String(paused); }
      this.attr(toggle, 'aria-label', isJa() ? (paused ? '動画を再生' : '動画を一時停止') : (paused ? 'Play video' : 'Pause video'));
      const coarse = matchMedia('(pointer:coarse)').matches;
      toggle.hidden = !coarse;
      const surface = node.querySelector('.lpd-video-touch-surface'); if (surface) surface.hidden = !coarse;
      video.controls = !coarse;
    };
    P.mediaVisibleV32 = function (media) {
      if (!this.isConnected || document.hidden || this.sizeGuideOpen || this.querySelector('dialog[open]')) return false;
      const node = media.closest('[data-lpd-index]');
      if (!node || Number(node.dataset.lpdIndex) !== this.mediaIndex) return false;
      // Do not depend on a stale gallery IntersectionObserver result during mobile layout/toolbar changes.
      const r = media.getBoundingClientRect(), t = this.track.getBoundingClientRect();
      const vv = window.visualViewport;
      const left = vv?.offsetLeft || 0, top = vv?.offsetTop || 0;
      const right = left + (vv?.width || document.documentElement.clientWidth);
      const bottom = top + (vv?.height || window.innerHeight);
      const width = Math.min(r.right, t.right, right) - Math.max(r.left, t.left, left);
      const height = Math.min(r.bottom, t.bottom, bottom) - Math.max(r.top, t.top, top);
      return r.width > 0 && r.height > 0 && width >= Math.min(r.width * 0.5, 120) && height > 16;
    };
    P.pauseVideo = function (video) {
      const state = stateOf(video);
      clearTimeout(state.retry); state.retry = null;
      if (video.paused && !state.pending) return;
      // Invalidate an in-flight play request so an old promise cannot re-block a later request.
      state.pending = null; state.internalPause = true; this.automaticPause = true;
      video.pause();
      setTimeout(() => { state.internalPause = false; this.automaticPause = false; }, 0);
    };
    P.playVideoV31 = function (video, userGesture = false) {
      const state = stateOf(video);
      if (!this.mediaVisibleV32(video)) return;
      if (!userGesture && (state.pending || state.blocked || state.pausedByUser || state.tries >= 3)) return;
      if (userGesture) {
        // A pending automatic play() must never consume the explicit Play button's user gesture.
        state.pending = null; state.blocked = false; state.tries = 0; state.pausedByUser = false;
        this.userPausedVideo = null;
      }
      clearTimeout(state.retry); state.retry = null;
      if (video.closest('[data-lpd-index]')?.dataset.lpdIndex === '0') {
        video.muted = true; video.defaultMuted = true; video.setAttribute('muted', '');
      }
      video.playsInline = true; video.setAttribute('playsinline', ''); video.setAttribute('webkit-playsinline', '');
      const token = {}; state.pending = token; state.tries += 1; state.lastError = '';
      let result;
      try { result = video.play(); } catch (error) { result = Promise.reject(error); }
      Promise.resolve(result).then(() => {
        if (state.pending !== token) return;
        state.pending = null; state.blocked = false; state.tries = 0;
        if (!this.mediaVisibleV32(video)) this.pauseVideo(video);
      }, error => {
        if (state.pending !== token) return;
        state.pending = null; state.lastError = error?.name || 'PlayError';
        state.blocked = error?.name === 'NotAllowedError';
        // Do not retry a policy denial. Recover only transient loading/aborted-play failures, with a strict limit.
        if (!state.blocked && state.tries < 3 && this.mediaVisibleV32(video) && !state.pausedByUser) {
          state.retry = setTimeout(() => {
            state.retry = null;
            if (this.isConnected) this.playVideoV31(video);
          }, 300 * state.tries);
        }
      }).finally(() => { this.syncVideoControlV31(video); this.updateAutoplayReportV32(); });
    };
    P.updateMediaPlayback = function () {
      if (!this.ready || !this.mediaNodes) return;
      this.mediaNodes.forEach((node, index) => {
        node.querySelectorAll('video').forEach(video => {
          const state = stateOf(video), visible = this.mediaVisibleV32(video);
          if (visible && !state.visible) { state.tries = 0; }
          state.visible = visible;
          if (!visible) {
            video.autoplay = false; this.pauseVideo(video); this.syncVideoControlV31(video); return;
          }
          if (index === 0) {
            video.autoplay = !state.pausedByUser && !state.blocked;
            if (video.paused) this.playVideoV31(video);
          }
          this.syncVideoControlV31(video);
        });
        node.querySelectorAll('iframe[data-lpd-external]').forEach(frame => {
          const play = index === 0 && this.mediaVisibleV32(frame);
          if (play && !frame.dataset.lpdAutoConfigured) {
            try { const u = new URL(frame.src); u.searchParams.set('autoplay', '1'); u.searchParams.set(frame.dataset.lpdExternal === 'vimeo' ? 'muted' : 'mute', '1'); frame.dataset.lpdAutoConfigured = 'true'; frame.src = u.href; } catch (_) {}
          } else this.frameCommand(frame, play);
        });
      });
      this.updateAutoplayReportV32();
    };
    P.autoplayDiagnosticsV32 = function () {
      const video = this.mediaNodes?.[0]?.querySelector('video'); if (!video) return null;
      const s = stateOf(video);
      const policy = document.permissionsPolicy || document.featurePolicy;
      let allowed = null; try { if (policy?.allowsFeature) allowed = policy.allowsFeature('autoplay'); } catch (_) {}
      return { revision: '3.3', reducedMotion: this.reduceMotion.matches, reducedMotionBlocksAutoplay: false, visible: this.mediaVisibleV32(video),
        paused: video.paused, started: s.started, time: +video.currentTime.toFixed(1), readyState: video.readyState,
        networkState: video.networkState, muted: video.muted, playsInline: video.playsInline,
        blocked: s.blocked, pending: !!s.pending, manualPause: s.pausedByUser, lastError: s.lastError,
        autoplayPermission: allowed, hasSource: !!(video.currentSrc || video.getAttribute('src') || video.querySelector('source[src]')) };
    };
    P.setupAutoplayReportV32 = function () {
      if (this.dataset.lpdAutoplayPreview !== 'true' || !this.mediaNodes?.[0]?.querySelector('video')) return;
      const note = document.createElement('details'); note.hidden = true; note.dataset.lpdAutoplayReport = '';
      note.style.cssText = 'margin:8px 16px;padding:8px 10px;border:1px solid #ddd;font-size:12px;line-height:1.7;background:#fff;color:#333';
      const summary = document.createElement('summary'); summary.textContent = '動画の再生状況（テスト用）';
      const text = document.createElement('p'); text.style.cssText = 'margin:6px 0 0;white-space:normal';
      note.append(summary, text); this.querySelector('.lpd-gallery-stage')?.append(note);
      this.autoplayReportV32 = note;
      this.on(note, 'toggle', () => this.updateAutoplayReportV32());
      this.autoplayReportSinceV32 = performance.now();
    };
    P.updateAutoplayReportV32 = function () {
      const panel = this.autoplayReportV32; if (!panel) return;
      const d = this.autoplayDiagnosticsV32(); if (!d) return;
      const waiting = d.paused || d.readyState < 2 || d.time === 0;
      panel.hidden = !waiting || performance.now() - this.autoplayReportSinceV32 < 5000;
      let reason = '動画データの読み込み待ち';
      if (d.manualPause) reason = '再生ボタンで一時停止されています';
      else if (d.autoplayPermission === false) reason = 'このプレビュー枠では自動再生が許可されていません';
      else if (d.blocked) reason = 'ブラウザが自動再生を拒否しました（NotAllowedError）';
      else if (!d.visible) reason = '動画が画面内に表示されるのを待っています';
      else if (d.lastError) reason = '再生処理のエラー：' + d.lastError;
      else if (!d.hasSource) reason = '動画の読み込み先を確認できません';
      else if (d.readyState >= 2) reason = d.started ? '動画が停止しています' : '動画データの読み込み完了・再生待ち';
      const text = reason + ' ｜ V' + d.revision + '・読み込み ' + d.readyState + '/4・再生位置 ' + d.time + '秒';
      const target = panel.querySelector('p'); if (target.textContent !== text) target.textContent = text;
    };
    P.handleClick = function (event) {
      const toggle = event.target.closest('[data-lpd-video-toggle]');
      if (toggle && this.contains(toggle)) {
        event.preventDefault();
        const video = toggle.closest('[data-lpd-media]')?.querySelector('video'); if (!video) return;
        const state = stateOf(video);
        if (video.paused || video.ended || state.pending) this.playVideoV31(video, true);
        else { state.pausedByUser = true; video.autoplay = false; this.pauseVideo(video); }
        this.syncVideoControlV31(video); return;
      }
      click.call(this, event);
    };
    P.disconnectedCallback = function () { (this.autoplayTimersV32 || []).forEach(clearTimeout); cancelAnimationFrame(this.autoplayFrameV32); this.autoplayReportV32?.remove(); this.descriptionObserverV31?.disconnect(); this.v31Ready = false; this.v3Ready = false; disconnect.call(this); };
    document.querySelectorAll('londa-product').forEach(el => { if (el.ready) el.refineV31(); });
  });
})();
